import { Alpine } from '../../core/alpine-core.js'
import { unwrapApiEntity } from '../../core/api-entity.js'
import { apiFetch } from '../api.js'
import { venuePersonaAvatarDataUri } from '../../public/venue-kiosk-avatars.js'
import { buildPersonaAvatarChoices, newPersonaSeed, personaAvatarUrl } from './player-avatar-bank.js'
import { namesFromRandomPick, pickRandomPlayerName } from '../../core/random-player-names.js'
import { PLAYER_COUNTRIES, countryLabelForCode, filterCountries } from '../../core/player-countries.js'
import { ensurePlayerNickname } from '../../core/player-nickname.js'

Alpine.data('playersEdit', () => ({

    teams: [],
    tab: 'profile',
    isEditing: false,
    saving: { profile: false, contact: false, team: false, setup: -1 },
    removing: { setup: -1 },

    statusOptions: ['active', 'inactive', 'reserve', 'pool'],
    weightUnits: ['g', 'oz'],
    lengthUnits: ['mm', 'in'],
    throwStyles: ['straight', 'arc', 'lob', 'other'],
    gripTypes: ['ring', 'micro', 'shark', 'knurl', 'scallop', 'milled', 'smooth', 'mixed'],
    pointTypes: ['smooth', 'grooved', 'storm', 'conversion'],
    pointMaterials: ['steel', 'titanium', 'tungsten', 'other'],
    pointGrips: ['none', 'micro', 'rings', 'diamond', 'milled', 'other'],
    shaftMaterials: ['nylon', 'polycarbonate', 'aluminum', 'carbon', 'titanium', 'other'],
    shaftLengthLabels: ['extra-short', 'short', 'inbetween', 'medium', 'long'],
    flightShapes: ['standard', 'no2', 'no6', 'kite', 'pear', 'slim', 'teardrop', 'vapor', 'other'],
    flightTextures: ['smooth', 'dimple', 'textured', 'fabric', 'lisa', 'other'],

    form: {
        _id: null,
        fullName: '',
        nickname: '',
        nicknameSlug: null,
        country: 'GB',
        imageUrl: '',
        status: 'pool',
        registeredTeamId: '',
        phone: '',
        email: '',
        loginEnabled: true,
        notes: '',
        dartSetups: [],
        role: 'player',
    },

    avatarMode: 'bank',
    personaSeed: '',
    avatarChoices: [],

    countrySearch: '',
    countryPickerOpen: false,

    teamDetail: null,
    teamRoster: [],
    /** Captain id from player/team load — used before teamDetail is ready. */
    loadedTeamCaptainId: null,
    teamSearch: { q: '', results: [], open: false },
    busy: { team: false, search: false, captain: false },

    deleteOpen: false,
    deleteBusy: false,
    loading: true,
    roleOptions: ['player', 'editor', 'admin'],
    countryOptions: PLAYER_COUNTRIES,

    ui: { showCreatePw: false },
    sendInvite: false,
    createPw: { pw: '', confirm: '' },
    resetPw: { busy: false, done: false, error: '' },

    get exists() { return !!this.form._id },

    isEditingSelf() {
        try {
            return !!Alpine.store('session')?.isSelf?.(this.form._id)
        } catch {
            return false
        }
    },

    pageTitle() {
        if (!this.exists) return 'Add player'
        return this.isEditingSelf() ? 'Your Profile' : 'Edit player'
    },

    pageSubtitle() {
        if (this.isEditingSelf()) {
            return 'Your league profile, team, darts setup, and password reset.'
        }
        return 'Arcade profile card, avatar bank, team assignment, darts & password.'
    },
    get canEditContact() { return this.exists },
    get canEditDarts() { return this.exists },
    get canResetPassword() { return this.exists },

    get canEdit() {
        try {
            const S = Alpine.store('session')
            if (!S?.canEditRosterPlayer) return false
            return S.canEditRosterPlayer(this.form._id)
        } catch {
            return false
        }
    },

    get canEditRoles() {
        try {
            const me = Alpine.store('session')?.me
            return !!me && me.role === 'admin' && this.exists
        } catch { return false }
    },

    get previewSubject() {
        return this.currentPlayerCard()
    },

    get teamMates() {
        const selfId = String(this.form._id || '').trim()
        const others = this.teamRoster.filter((p) => String(p._id || '') !== selfId)
        return others.sort((a, b) => {
            const aCap = this.isTeamCaptain(a) ? 0 : 1
            const bCap = this.isTeamCaptain(b) ? 0 : 1
            return aCap - bCap
        })
    },

    hasTeamAssignment() {
        return !!String(this.form.registeredTeamId || '').trim()
    },

    previewTeamId() {
        return String(this.form.registeredTeamId || '').trim()
    },

    get canSaveProfile() {
        if (!this.form.fullName?.trim()) return false
        if (!String(this.form.country || '').trim()) return false
        if (!this.exists) {
            if (this.sendInvite) {
                if (!String(this.form.email || '').trim()) return false
            } else if (this.hasPasswordErr()) {
                return false
            }
        }
        return true
    },

    sanitizeNickname(v = '') {
        return String(v || '')
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/[^a-z0-9._-]/g, '')
    },

    onNicknameInput(e) {
        const next = this.sanitizeNickname(e?.target?.value ?? this.form.nickname)
        this.form.nickname = next
        if (e?.target && e.target.value !== next) e.target.value = next
    },

    countryLabel(code) {
        return countryLabelForCode(code, this.countryOptions)
    },

    filteredCountryOptions() {
        return filterCountries(this.countrySearch, this.countryOptions)
    },

    openCountryPicker() {
        this.countryPickerOpen = true
        this.countrySearch = ''
        Alpine.nextTick(() => this.$refs?.countrySearchInput?.focus?.())
    },

    selectCountry(code) {
        const c = String(code || '').trim().toUpperCase().slice(0, 2)
        if (!c) return
        this.form.country = c
        this.countryPickerOpen = false
        this.countrySearch = ''
    },

    hasPasswordErr() {
        if (this.exists || this.sendInvite) return false
        if (!this.createPw.pw || this.createPw.pw.length < 8) return 'Password must be at least 8 characters'
        if (this.createPw.pw !== this.createPw.confirm) return 'Passwords do not match'
        return ''
    },

    readRouteId() {
        const url = new URL(location.href)
        const idFromQuery = url.searchParams.get('id')
        if (idFromQuery) return idFromQuery
        const teamPrefill = url.searchParams.get('teamId')
        if (teamPrefill) this.form.registeredTeamId = teamPrefill

        const path = (url.hash && url.hash.startsWith('#/')) ? url.hash.slice(1) : url.pathname
        const parts = path.split('/').filter(Boolean)
        const idx = parts.indexOf('players')
        if (idx >= 0 && parts[idx + 1]) {
            const seg = decodeURIComponent(parts[idx + 1])
            return seg === 'new' ? null : seg
        }
        return null
    },

    viewUrl() {
        return this.form._id ? `/admin/players/${encodeURIComponent(this.form._id)}` : '/admin/players'
    },

    playerLabel(p) {
        if (!p) return 'Player'
        const name = p.fullName || p.displayName || 'Player'
        return p.nickname ? `${name} (@${p.nickname})` : name
    },

    currentPlayerCard() {
        return {
            _id: this.form._id,
            fullName: this.form.fullName,
            nickname: this.form.nickname,
            imageUrl: this.form.imageUrl,
            status: this.form.status,
            registeredTeamId: this.form.registeredTeamId || '',
            country: this.form.country,
            stats: this.form.stats || {},
            skillLevel: this.form.skillLevel || null,
        }
    },

    profileAvatarUrl(player) {
        const url = String(player?.imageUrl || '').trim()
        if (url) return url
        const seed = String(player?._id || this.personaSeed || 'player')
        return venuePersonaAvatarDataUri(seed)
    },

    onProfileAvatarError(e) {
        const img = e?.target
        if (!img) return
        img.onerror = null
        img.src = venuePersonaAvatarDataUri(String(this.form._id || this.personaSeed || 'player'))
    },

    skillLevelLabel(level) {
        const s = String(level || '').trim().toLowerCase()
        if (s === 'beginner') return 'Beginner'
        if (s === 'intermediate') return 'Intermediate'
        if (s === 'advanced') return 'Advanced'
        if (s === 'pro') return 'Pro'
        return level ? String(level) : 'League'
    },

    skillLevelClass(level) {
        const s = String(level || 'intermediate').trim().toLowerCase()
        return `challenge-duel-skill challenge-duel-skill--${s}`
    },

    statusLabel(status) {
        const s = String(status || 'pool').toLowerCase()
        if (s === 'active') return 'Roster'
        if (s === 'reserve') return 'Reserve'
        if (s === 'inactive') return 'Inactive'
        return 'Pool'
    },

    setAvatarMode(mode) {
        this.avatarMode = mode === 'upload' ? 'upload' : 'bank'
        if (this.avatarMode === 'bank') this.rebuildAvatarChoices()
    },

    rebuildAvatarChoices(seed) {
        const cur = String(seed || this.personaSeed || this.form._id || newPersonaSeed())
        this.personaSeed = cur
        this.avatarChoices = buildPersonaAvatarChoices(cur)
        this.syncAvatarSelection()
    },

    shuffleAvatarBank() {
        this.rebuildAvatarChoices(newPersonaSeed())
    },

    syncAvatarSelection() {
        const choice = this.avatarChoices.find((c) => c.seed === this.personaSeed) || this.avatarChoices[0]
        if (!choice) return
        this.personaSeed = choice.seed
        this.form.imageUrl = choice.url
    },

    selectAvatarChoice(choice) {
        if (!choice?.seed) return
        this.personaSeed = choice.seed
        this.form.imageUrl = choice.url
    },

    isAvatarSelected(choice) {
        return !!choice?.seed && choice.seed === this.personaSeed
    },

    generateRandomNickname() {
        const { fullName, nickname } = namesFromRandomPick()
        this.form.nickname = this.sanitizeNickname(nickname)
        if (!String(this.form.fullName || '').trim()) {
            this.form.fullName = fullName
        }
    },

    generateRandomFullName() {
        this.form.fullName = pickRandomPlayerName()
        if (!this.sanitizeNickname(this.form.nickname)) {
            this.form.nickname = this.sanitizeNickname(this.form.fullName.split(/\s+/)[0] || '')
        }
    },

    normalizeTeamAssignment() {
        if (!this.hasTeamAssignment()) {
            this.form.status = 'pool'
            return
        }
        if (String(this.form.status || '').toLowerCase() === 'pool') {
            this.form.status = 'active'
        }
    },

    applyPersonaSeed() {
        this.form.imageUrl = personaAvatarUrl(this.personaSeed)
    },

    initAvatarFromPlayer() {
        const url = String(this.form.imageUrl || '').trim()
        const isRemote = /^https?:\/\//i.test(url)
        if (isRemote) {
            this.avatarMode = 'upload'
            this.personaSeed = String(this.form._id || newPersonaSeed())
            return
        }
        if (url.startsWith('data:image')) {
            this.avatarMode = 'bank'
            this.personaSeed = String(this.form._id || newPersonaSeed())
            this.rebuildAvatarChoices(this.personaSeed)
            return
        }
        this.avatarMode = 'bank'
        this.personaSeed = String(this.form._id || newPersonaSeed())
        if (!url) this.applyPersonaSeed()
        this.rebuildAvatarChoices(this.personaSeed)
    },

    async init(id = null) {
        const routeId = id ?? this.readRouteId()
        await this.loadTeams()
        this.isEditing = Boolean(routeId)
        this.normalizeTeamAssignment()

        if (this.isEditing) {
            await this.load(routeId)
            if (!this.canEdit) {
                const viewId = encodeURIComponent(String(this.form._id || routeId || ''))
                globalThis.location.replace(`/admin/players/${viewId}`)
                return
            }
        } else {
            this.tab = 'profile'
            this.normalizeTeamAssignment()
            this.personaSeed = newPersonaSeed()
            this.applyPersonaSeed()
            this.rebuildAvatarChoices(this.personaSeed)
        }
        this.loading = false
        try {
            document.title = `${this.pageTitle()} — DartsOS`
        } catch {
            /* ignore */
        }
    },

    async load(id) {
        try {
            const res = await apiFetch(`/players/${id}`)
            const data = await res.json()
            if (!res.ok) throw new Error(data.message || 'Failed to load player')
            const p = unwrapApiEntity(data, ['player', 'item']) || data
            this.form = {
                _id: p._id,
                fullName: p.fullName || '',
                nickname: this.sanitizeNickname(p.nickname || ''),
                nicknameSlug: p.nicknameSlug ?? null,
                country: p.country || 'GB',
                imageUrl: p.imageUrl || '',
                status: p.status || 'pool',
                registeredTeamId: p.registeredTeamId || '',
                phone: p.phone || '',
                email: p.email || '',
                loginEnabled: p.loginEnabled !== false,
                notes: p.notes || '',
                dartSetups: (Array.isArray(p.dartSetups) ? p.dartSetups : []).map((x) => this.ensureSetupShape(x)),
                role: p.role || 'player',
                stats: p.stats || {},
                skillLevel: p.skillLevel || null,
            }
            this.initAvatarFromPlayer()
            this.ensureSinglePrimary()
            this.normalizeTeamAssignment()
            const teamMeta = p.team && typeof p.team === 'object' ? p.team : null
            this.loadedTeamCaptainId = String(
                teamMeta?.captainPlayerId?._id || teamMeta?.captainPlayerId || '',
            ).trim() || null
            if (this.form.registeredTeamId) {
                await this.loadTeamContext()
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Failed to load player')
        }
    },

    async loadTeams() {
        try {
            const r = await apiFetch('/teams?limit=1000&active=true')
            const d = await r.json()
            if (!r.ok) throw new Error(d.message || 'Failed to load teams')
            this.teams = Array.isArray(d.items) ? d.items : []
        } catch (e) {
            console.warn('loadTeams:', e)
            this.teams = []
        }
    },

    teamName(teamId) {
        const id = String(teamId || '').trim()
        if (!id) return '—'
        return this.teams.find((t) => String(t._id) === id)?.name || 'Team'
    },

    isTeamCaptain(player) {
        const cap = String(this.teamDetail?.captainPlayerId || this.loadedTeamCaptainId || '').trim()
        return cap && String(player?._id || '') === cap
    },

    isSelfCaptain() {
        return this.isTeamCaptain({ _id: this.form._id })
    },

    /** Captain cannot change team, roster slot, or leave the team from this screen. */
    captainTeamLocked() {
        return this.isSelfCaptain()
    },

    async onTeamChange() {
        if (this.captainTeamLocked()) return
        this.normalizeTeamAssignment()
        await this.loadTeamContext()
    },

    async loadTeamContext() {
        const teamId = String(this.form.registeredTeamId || '').trim()
        if (!teamId) {
            this.teamDetail = null
            this.teamRoster = []
            this.loadedTeamCaptainId = null
            return
        }
        this.busy.team = true
        try {
            const [teamRes, rosterRes] = await Promise.all([
                apiFetch(`/teams/${encodeURIComponent(teamId)}`),
                apiFetch(`/teams/${encodeURIComponent(teamId)}/roster`),
            ])
            const teamData = await teamRes.json().catch(() => ({}))
            const rosterData = await rosterRes.json().catch(() => ({}))
            if (teamRes.ok) {
                const t = unwrapApiEntity(teamData, ['team', 'item']) || teamData
                this.teamDetail = {
                    _id: t._id,
                    name: t.name,
                    captainPlayerId: String(
                        t.captainPlayerId?._id || t.captainPlayerRef || t.captainPlayerId || rosterData.captainPlayerId || '',
                    ).trim() || null,
                }
                this.loadedTeamCaptainId = this.teamDetail.captainPlayerId
            }
            if (rosterRes.ok) {
                const roster = Array.isArray(rosterData.roster) ? rosterData.roster : []
                const reserves = Array.isArray(rosterData.reserves) ? rosterData.reserves : []
                this.teamRoster = [...roster, ...reserves]
            } else {
                this.teamRoster = []
            }
        } catch (e) {
            console.warn('loadTeamContext', e)
            this.teamRoster = []
        } finally {
            this.busy.team = false
        }
    },

  closeTeamSearch() {
        this.teamSearch.open = false
    },

    async searchPoolPlayers() {
        const q = String(this.teamSearch.q || '').trim()
        if (!q) {
            this.teamSearch.results = []
            this.teamSearch.open = false
            return
        }
        this.busy.search = true
        try {
            const r = await apiFetch(
                `/players?q=${encodeURIComponent(q)}&limit=20&sortBy=fullName:asc&select=_id,fullName,nickname,imageUrl,registeredTeamId,status,stats,skillLevel`,
            )
            const d = await r.json()
            this.teamSearch.results = Array.isArray(d.items) ? d.items : []
            this.teamSearch.open = this.teamSearch.results.length > 0
        } catch {
            this.teamSearch.results = []
            this.teamSearch.open = false
        } finally {
            this.busy.search = false
        }
    },

    setPlayerImage(url) {
        this.form.imageUrl = url
        this.avatarMode = 'upload'
        const S = Alpine.store('session')
        const me = S?.me
        if (me && String(me._id) === String(this.form._id)) {
            const updated = { ...me, imageUrl: url }
            S.set({ token: S.token, me: updated })
        }
        if (this.exists) {
            this.saveProfile({ silent: true })
            Alpine.store('session').refreshIfSelf(this.form._id)
        }
    },

    resolvedNickname() {
        return ensurePlayerNickname(this.form.fullName, this.form.nickname)
    },

    profilePayload() {
        const payload = {
            fullName: this.form.fullName.trim(),
            nickname: this.resolvedNickname(),
            country: String(this.form.country || 'GB').trim().toUpperCase().slice(0, 2),
            imageUrl: this.form.imageUrl?.trim() || null,
            role: this.form.role,
            notes: this.form.notes?.trim() || null,
        }
        if (!this.captainTeamLocked()) {
            payload.status = this.form.status
            payload.registeredTeamId = String(this.form.registeredTeamId || '').trim() || null
        }
        if (this.avatarMode === 'bank' && this.personaSeed) {
            payload.imageUrl = personaAvatarUrl(this.personaSeed)
        }
        return payload
    },

    async saveProfile(opts = {}) {
        const silent = !!opts.silent
        if (!silent) this.saving.profile = true
        try {
            if (!this.form.fullName?.trim()) throw new Error('Full Name is required.')
            if (!String(this.form.country || '').trim()) throw new Error('Country is required.')
            this.form.nickname = this.resolvedNickname()
            if (this.avatarMode === 'bank') this.applyPersonaSeed()

            const payload = this.profilePayload()

            if (!this.exists) {
                const pwErr = this.hasPasswordErr()
                if (pwErr) throw new Error(pwErr)
                if (this.sendInvite && !this.form.email?.trim()) {
                    throw new Error('Email is required to send an invite.')
                }

                const body = this.sendInvite
                    ? {
                        ...payload,
                        sendInvite: true,
                        email: this.form.email.trim().toLowerCase(),
                        phone: this.form.phone?.trim() || null,
                        loginEnabled: false,
                    }
                    : {
                        ...payload,
                        password: this.createPw.pw,
                        email: this.form.email?.trim() || null,
                        phone: this.form.phone?.trim() || null,
                        loginEnabled: true,
                    }

                const r = await apiFetch('/players', { method: 'POST', body })
                const d = await r.json().catch(() => ({}))
                if (!r.ok) throw new Error(d.message || 'Create failed')

                const createdId = String(d.item?._id || d._id || d.player?._id || '').trim()
                if (createdId && payload.registeredTeamId) {
                    await apiFetch(`/teams/${encodeURIComponent(payload.registeredTeamId)}/roster`, {
                        method: 'POST',
                        body: { playerId: createdId },
                    }).catch(() => {})
                }

                if (!silent) {
                    if (d.inviteSent) this.$store.toastr.success('Player created — invite email sent.')
                    else this.$store.toastr.success('Player created')
                }
                location.href = createdId
                    ? `/admin/players/${encodeURIComponent(createdId)}/edit`
                    : '/admin/players'
                return
            }

            const r = await apiFetch(`/players/${this.form._id}/profile`, { method: 'PATCH', body: payload })
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d.message || 'Save failed')

            await Alpine.store('session').refreshIfSelf(this.form._id)
            if (!silent) this.$store.toastr.success('Player updated')
            if (payload.registeredTeamId) await this.loadTeamContext()
        } catch (e) {
            if (!silent) this.$store.toastr.error(e.message || 'Save failed')
        } finally {
            if (!silent) this.saving.profile = false
        }
    },

    setRosterStatus(status) {
        if (this.captainTeamLocked()) return
        const next = String(status || '').toLowerCase()
        if (!this.hasTeamAssignment()) {
            if (next === 'pool') this.form.status = 'pool'
            return
        }
        if (next === 'pool') return
        if (next === 'active' || next === 'reserve') {
            this.form.status = next
        }
    },

    openProfileTab() {
        this.tab = 'profile'
    },

    async saveTeam() {
        if (!this.exists) {
            this.$store.toastr.warning('Create the player first on the Profile tab.')
            this.tab = 'profile'
            return
        }
        if (this.captainTeamLocked()) {
            this.$store.toastr.warning('Team captain assignment is locked. Appoint a new captain on the team page first.')
            return
        }
        this.saving.team = true
        try {
            const teamId = String(this.form.registeredTeamId || '').trim() || null
            const status = String(this.form.status || 'pool')

            if (teamId) {
                await apiFetch(`/teams/${encodeURIComponent(teamId)}/roster`, {
                    method: 'POST',
                    body: { playerId: this.form._id },
                })
            }

            const r = await apiFetch(`/players/${this.form._id}/profile`, {
                method: 'PATCH',
                body: {
                    registeredTeamId: teamId,
                    status,
                },
            })
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d.message || 'Team save failed')

            await Alpine.store('session').refreshIfSelf(this.form._id)
            await this.loadTeamContext()
            this.$store.toastr.success('Team assignment saved')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Team save failed')
        } finally {
            this.saving.team = false
        }
    },

    async makeTeamCaptain() {
        if (!this.exists) {
            this.$store.toastr.warning('Save the player first.')
            return
        }
        if (this.isSelfCaptain()) return
        const teamId = String(this.form.registeredTeamId || '').trim()
        if (!teamId) {
            this.$store.toastr.warning('Choose a team first.')
            return
        }
        this.busy.captain = true
        try {
            await this.saveTeam()
            const r = await apiFetch(`/teams/${encodeURIComponent(teamId)}`, {
                method: 'PATCH',
                body: { captainPlayerId: this.form._id },
            })
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d.message || 'Could not set captain')
            this.form.status = 'active'
            await apiFetch(`/players/${this.form._id}/profile`, {
                method: 'PATCH',
                body: { status: 'active', registeredTeamId: teamId },
            })
            await this.loadTeamContext()
            this.$store.toastr.success('Captain updated')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Captain update failed')
        } finally {
            this.busy.captain = false
        }
    },

    async removeFromTeam() {
        if (!this.exists || !this.form.registeredTeamId) return
        if (this.isSelfCaptain()) {
            this.$store.toastr.warning('Assign a new captain before removing the current captain from the team.')
            return
        }
        this.saving.team = true
        try {
            await apiFetch(`/teams/${encodeURIComponent(this.form.registeredTeamId)}/roster`, {
                method: 'DELETE',
                body: { playerId: this.form._id },
            })
            this.form.registeredTeamId = ''
            this.form.status = 'pool'
            this.normalizeTeamAssignment()
            await apiFetch(`/players/${this.form._id}/profile`, {
                method: 'PATCH',
                body: { registeredTeamId: null, status: 'pool' },
            })
            this.teamDetail = null
            this.teamRoster = []
            this.loadedTeamCaptainId = null
            this.$store.toastr.success('Removed from team')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Remove failed')
        } finally {
            this.saving.team = false
        }
    },

    get resetPasswordTargetEmail() {
        return String(this.form.email || '').trim().toLowerCase()
    },

    canRequestPasswordReset() {
        const email = this.resetPasswordTargetEmail
        return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !!this.form.loginEnabled
    },

    async requestPasswordResetEmail() {
        if (!this.exists) {
            this.$store.toastr.warning('Create the player first.')
            return
        }
        const email = this.resetPasswordTargetEmail
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            this.$store.toastr.error('Add a valid email on the Contact tab first.')
            return
        }
        if (!this.form.loginEnabled) {
            this.$store.toastr.warning('Portal login is disabled for this player.')
            return
        }
        this.resetPw.busy = true
        this.resetPw.done = false
        this.resetPw.error = ''
        try {
            const r = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ email, mode: 'link' }),
            })
            const d = await r.json().catch(() => ({}))
            if (r.status === 429) {
                throw new Error(d.message || 'Too many requests. Please try again later.')
            }
            if (!r.ok) {
                throw new Error(d.message || 'Could not send reset email.')
            }
            this.resetPw.done = true
            const msg = d.message || 'If an account exists for that email, we sent instructions.'
            this.$store.toastr.success(msg)
        } catch (e) {
            this.resetPw.error = e.message || 'Could not send reset email.'
            this.$store.toastr.error(this.resetPw.error)
        } finally {
            this.resetPw.busy = false
        }
    },

    async saveContact() {
        if (!this.exists) {
            this.$store.toastr.warning('Create the player first.')
            return
        }
        this.saving.contact = true
        try {
            const payload = {
                phone: this.form.phone?.trim() || null,
                email: this.form.email?.trim() || null,
                loginEnabled: !!this.form.loginEnabled,
            }
            const r = await apiFetch(`/players/${this.form._id}/contact`, { method: 'PATCH', body: payload })
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d.message || 'Save failed')
            await Alpine.store('session').refreshIfSelf(this.form._id)
            this.$store.toastr.success('Contact saved')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Save failed')
        } finally {
            this.saving.contact = false
        }
    },

    ensureSinglePrimary() {
        let found = false
        for (const s of this.form.dartSetups) {
            if (s.isPrimary && !found) found = true
            else s.isPrimary = false
        }
        if (!found && this.form.dartSetups.length) {
            this.form.dartSetups[0].isPrimary = true
        }
    },

    ensureSetupShape(s) {
        if (!s.barrels) s.barrels = { weightUnit: 'g', lengthUnit: 'mm' }
        if (!s.points) s.points = { lengthUnit: 'mm' }
        if (!s.shafts) s.shafts = { lengthUnit: 'mm' }
        if (!s.flights) s.flights = {}
        if (!s.totalWeightUnit) s.totalWeightUnit = 'g'
        return s
    },

    buildSetupPayload(s) {
        s = this.ensureSetupShape(s)
        const j = (o) => JSON.parse(JSON.stringify(o ?? {}))
        const payload = {
            _id: s._id || undefined,
            name: s.name ?? '',
            isPrimary: !!s.isPrimary,
            totalWeight: s.totalWeight ?? null,
            totalWeightUnit: s.totalWeightUnit ?? 'g',
            hand: s.hand ?? '',
            throwStyle: s.throwStyle ?? '',
            imageUrl: s.imageUrl ?? '',
        }
        if (s.barrels) {
            payload.barrels = j({
                brand: s.barrels.brand,
                model: s.barrels.model,
                tungstenPercent: s.barrels.tungstenPercent,
                weight: s.barrels.weight,
                weightUnit: s.barrels.weightUnit,
                length: s.barrels.length,
                lengthUnit: s.barrels.lengthUnit,
                gripType: s.barrels.gripType,
                balance: s.barrels.balance,
                finish: s.barrels.finish,
                notes: s.barrels.notes,
            })
        }
        if (s.points) {
            payload.points = j({
                brand: s.points.brand,
                model: s.points.model,
                type: s.points.type,
                material: s.points.material,
                length: s.points.length,
                lengthUnit: s.points.lengthUnit,
                grip: s.points.grip,
                notes: s.points.notes,
            })
        }
        if (s.shafts) {
            payload.shafts = j({
                brand: s.shafts.brand,
                model: s.shafts.model,
                material: s.shafts.material,
                lengthLabel: s.shafts.lengthLabel,
                length: s.shafts.length,
                lengthUnit: s.shafts.lengthUnit,
                notes: s.shafts.notes,
            })
        }
        if (s.flights) {
            payload.flights = j({
                brand: s.flights.brand,
                model: s.flights.model,
                shape: s.flights.shape,
                texture: s.flights.texture,
                notes: s.flights.notes,
            })
        }
        return payload
    },

    async saveSetup(idx) {
        if (!this.exists) {
            this.$store.toastr.warning('Create the player first.')
            return
        }
        this.saving.setup = idx
        try {
            const base = `/players/${this.form._id}/dart-setups`
            const s = this.ensureSetupShape(this.form.dartSetups[idx])
            const payload = this.buildSetupPayload(s)
            let r
            if (s._id) {
                r = await apiFetch(`${base}/${s._id}`, { method: 'PATCH', body: payload })
            } else {
                r = await apiFetch(base, { method: 'POST', body: payload })
            }
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d.message || 'Save failed')
            this.form.dartSetups[idx] = this.ensureSetupShape(d.setup || d)
            this.$store.toastr.success('Setup saved')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Save failed')
        } finally {
            this.saving.setup = -1
        }
    },

    async makePrimary(idx) {
        if (!this.exists) {
            this.$store.toastr.warning('Create the player first.')
            return
        }
        const s = this.form.dartSetups[idx]
        if (!s) return
        this.form.dartSetups.forEach((x, i) => { x.isPrimary = i === idx })
        this.ensureSinglePrimary()
        try {
            if (!s._id) await this.saveSetup(idx)
            const r = await apiFetch(`/players/${this.form._id}/dart-setups/${s._id}/primary`, { method: 'POST' })
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d.message || 'Failed to set primary')
            if (Array.isArray(d.dartSetups)) {
                this.form.dartSetups = d.dartSetups.map((x) => this.ensureSetupShape(x))
                this.ensureSinglePrimary()
            }
            this.$store.toastr.success('Primary setup updated')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Primary switch failed')
        }
    },

    async removeSetup(idx) {
        if (!this.exists) {
            this.$store.toastr.warning('Create the player first.')
            return
        }
        const s = this.form.dartSetups[idx]
        if (!s) return
        this.removing.setup = idx
        try {
            if (s._id) {
                await apiFetch(`/players/${this.form._id}/dart-setups/${s._id}`, { method: 'DELETE' })
            }
            this.form.dartSetups.splice(idx, 1)
            this.ensureSinglePrimary()
            this.$store.toastr.info('Setup deleted')
        } catch (e) {
            this.$store.toastr.error(e.message || 'Delete failed')
        } finally {
            this.removing.setup = -1
        }
    },

    addSetup() {
        const hasPrimary = this.form.dartSetups.some((x) => !!x.isPrimary)
        const setup = this.ensureSetupShape({
            name: '',
            isPrimary: !hasPrimary,
            totalWeight: null,
            totalWeightUnit: 'g',
            hand: '',
            throwStyle: '',
            imageUrl: '',
            barrels: { weightUnit: 'g', lengthUnit: 'mm' },
            points: { lengthUnit: 'mm' },
            shafts: { lengthUnit: 'mm' },
            flights: {},
        })
        this.form.dartSetups.push(setup)
        this.ensureSinglePrimary()
        this.$store.toastr.info('New setup added')
    },

    confirmDelete() { this.deleteOpen = true },

    async doDelete() {
        if (this.deleteBusy) return
        this.deleteBusy = true
        try {
            const r = await apiFetch(`/players/${this.form._id}`, { method: 'DELETE' })
            const d = await r.json().catch(() => ({}))
            if (!r.ok) throw new Error(d?.message || 'Delete failed')
            this.$store.toastr.success('Player removed')
            this.deleteOpen = false
            location.href = '/admin/players'
        } catch (e) {
            this.$store.toastr.error(e.message || 'Delete failed')
            this.deleteOpen = false
        } finally {
            this.deleteBusy = false
        }
    },
}))
